<script lang="ts">
  import type {IProvider} from "$lib/interfaces/IProvider";
  import ArrowRightIcon from "$lib/icons/ArrowRightIcon.svelte";
  import {createEventDispatcher, onMount} from "svelte";
  import {environment} from "$lib/env";
  import {translate} from "$lib/i18n";
  import {clickOutside} from "$lib/directives/clickOutside";
  import {page} from "$app/stores";

  let providers: IProvider[] = [];
  let selected: IProvider | null = $state(null);
  let search: string = "";
  let searchResults: IProvider[] = $state([]);
  let isOpen = $state(false);
  let dropdownRef: HTMLDivElement;
  let effectiveCategory: string | null = $state(null);

  interface Props {
    id: string;
    css?: string;
    category?: string | null; // Category slug passed from parent
  }

  let {
    id,
    css = 'bg-primary-dark -rota rounded-full py-4 transition-all px-8 flex items-center',
    category = $bindable(null)
  }: Props = $props();

  // Watch for changes to the category prop
  $effect(() => {
    updateEffectiveCategory();
  });

  // Watch for changes to the page URL
  $effect(() => {
    const pathname = $page.url.pathname;
    updateEffectiveCategory();
  });

  // Function to determine the effective category
  function updateEffectiveCategory() {
    // If category prop is provided, use it directly
    if (category !== null) {
      effectiveCategory = category;
      return;
    }

    // Otherwise, extract from URL path
    const pathParts = $page.url.pathname.split('/');
    const casinoIndex = pathParts.findIndex(part => part === 'casino');

    if (casinoIndex !== -1 && casinoIndex + 1 < pathParts.length) {
      const urlCategory = pathParts[casinoIndex + 1];

      // Validate that it's one of the allowed categories
      if (['slots', 'mini-games', 'live-casino'].includes(urlCategory)) {
        effectiveCategory = urlCategory;
        return;
      }
    }

    // Default to null if no valid category found
    effectiveCategory = null;
  }

  let promise = $state(null);
  const dispatch = createEventDispatcher();

  // Use $effect to watch for category changes and fetch providers
  $effect(() => {
    console.log("Effective category:", effectiveCategory);
    promise = getProviders();
  });

  function toggleDropdown() {
    console.log("Toggle dropdown clicked, current state:", isOpen);
    isOpen = !isOpen;
    console.log("New dropdown state:", isOpen);

    if (!isOpen) {
      search = '';
      searchResults = providers;
    }
  }

  async function getProviders() {
    // Build the URL with category filter if available
    let apiUrl = `${environment.apiUrl}/game-providers`;

    // Only add the category parameter if we have a valid category
    if (effectiveCategory) {
      apiUrl += `?gameProviderSlug=${effectiveCategory}`;
    }

    console.log(`Fetching providers with URL: ${apiUrl}`);

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        console.error(`API error: ${response.status} ${response.statusText}`);
        // If we get an error, try without the category parameter
        return fetch(`${environment.apiUrl}/game-providers`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }).then(r => r.json()).then(data => {
          providers = [...data.data];
          searchResults = data.data;
          selected = data.data.find((item: IProvider) => item.id === selected?.id) || {id: '', name: 'All', gameCount: 0, slug: 'all', imageUrl: ''};
          return data.data;
        });
      }

      const data = await response.json();
      providers = [...data.data];
      searchResults = data.data;
      selected = data.data.find((item: IProvider) => item.id === selected?.id) || {id: '', name: 'All', gameCount: 0, slug: 'all', imageUrl: ''};
      return data.data;
    } catch (error) {
      console.error("Error fetching providers:", error);
      // Return empty array to avoid breaking the UI
      return [];
    }
  }

  function searchProviders(value: string) {
    searchResults = providers.filter((item) => item.name.toLowerCase().includes(value.toLowerCase()));
  }

  function selectProvider(provider: IProvider) {
    selected = provider;
    dispatch('selected', provider);
    toggleDropdown();
  }

  onMount(() => {
    // Initialize the effective category and fetch providers
    updateEffectiveCategory();
    promise = getProviders();
  });
</script>

<div class="relative">
  <!-- Use onclick instead of on:click to match original behavior -->
  <button class="{css}" {id} name={id}
          onclick={() => toggleDropdown()}>
    {#if !selected || selected.id === ''}
      <span class="text-white mr-2">{$translate('Provider')}:</span>
    {/if}
    {#if selected && selected.id !== ''}
      <span class="text-primary-green text-nowrap">{selected.name}</span>
    {:else}
      <span class="text-primary-white">{$translate('All')}</span>
    {/if}
    <ArrowRightIcon css="ml-2 {!isOpen ? 'rotate-90' : '-rotate-90'}"/>
  </button>
  <div class="{isOpen ? 'absolute':'hidden'} top-16 animate-fadeIn z-50 left-0 md:-left-24 md:right-0 w-64 bg-primary-dark p-2 rounded-xl shadow-xl drop-shadow-lg border border-primary-lightBlue/20">
    <input type="text" placeholder="Search" oninput={(e) => searchProviders(e.currentTarget.value)}
           class="w-full p-2 bg-primary-dark outline-0 border-b border-primary-lightBlue text-white" autofocus />
    <div class="max-h-60 overflow-y-auto flex flex-col gap-2 pt-1">
      {#await promise}
        <div class="min-h-[60px] flex justify-center items-center col-span-full">
          <img src="/demo-logo.svg" class="animate-bounce" width="46" alt="Demo"/>
        </div>
      {:then providersLoaded}
        <div class="px-3 py-2.5 hover:bg-primary-blue cursor-pointer rounded-lg {selected?.id === '' ? 'text-primary-green bg-primary-blue/40' :'text-white'}"
             onclick={() => selectProvider({id: '', name: 'All', gameCount: 0, slug: 'all', imageUrl: ''})}>
          {$translate('All')}
        </div>
        {#each searchResults as provider}
          <div class="px-3 py-2.5 hover:bg-primary-blue cursor-pointer rounded-lg {selected?.id === provider.id ? 'text-primary-white bg-primary-blue/40' :'text-white'}"
               onclick={() => selectProvider(provider)}>
            {provider.name}
          </div>
        {/each}
      {:catch error}
        <p class="text-primary-fire">{error.message}</p>
      {/await}
    </div>
  </div>
</div>
